<template>
	<div class="coupon-container" :class="setResponsiveClass">
		<input class="input text-input coupon-input"
			@change="handleChangeCouponInput"
			@focus="handleChangeCouponInput"
			placeholder="Got a coupon code? Enter it here"
			aria-label="input coupon code"
			ref="couponInput" />
		<div class="coupon-message-container" ref="couponContainer">
			<BaseLoader v-if="showLoader()" type="input-field" />
			<div class="coupon-message" :class="messageClass">
				{{ displayMessage() }}

				<div class="coupon-icon-wrapper" v-if="applied">
					<BaseIcon icon="checkCircleLight" size="2x" />
				</div>

				<p id="redemptions-available" class="store-txt-color full-width">
					{{ getAvailableRedemptionsTxt() }}
				</p>
				<div class="store-txt-color">
					<menu-item-rows :hasDiscount="true" :items="items" v-if="items" />
				</div>
			</div>
			<a @click="handleRemoveCoupon" v-if="applied">Remove</a>
		</div>
	</div>
</template>

<script>
	import EventBus from '@EventBus';
	import couponMixin from '@Mixin/couponMixin';
	import discountMixin from '@Mixin/discountMixin';
	import checkoutMixin from '@Mixin/checkoutMixin';
	import MenuItemRows from '@/components/menu/common/MenuItemRows.vue';
	import Velocity from 'velocity-animate';
	import 'velocity-animate/velocity.ui.min';
	import { setTimeout } from 'timers';
	import CouponMessages from '@Messages/coupon';
	import Formatter from '@/utils/Formatter';
	import { findIndex, find } from 'lodash';
	import { ResponsiveMixin } from "vue-responsive-components";
	import itemDetailMixin from '../mixins/itemDetailMixin';
	import CartItemModel from '@/models/CartItemModel';

	const displayAnimation = 333;

	export default {
		mixins: [couponMixin, discountMixin, checkoutMixin, ResponsiveMixin, itemDetailMixin],
		breakpoints: {
			expandedView: (el) => {
				return (el.width >= 700);
			},
		},
		components: {
			'menu-item-rows': MenuItemRows,
		},
		data() {
			return {
				searching: false,
				items: null,
				applied: false,
			};
		},
		computed: {
			messageClass() {
				if (this.applied) {
					return 'coupon-applied';
				}
				return '';
			},
			setResponsiveClass() {
				if (this.el.is.expandedView) {
					return 'expanded';
				}
				return '';
			}
		},
		methods: {
			showLoader() {
				const searching = this.searching;
				const message = this.message;
				return (searching && !message);
			},
			handleChangeCouponInput(event) {
				const field = event.target;
				const value = field.value;
				const coupon = this.coupon;
				let oldValue = (coupon) ? coupon.codeName : '';
				if (value === '' || value !== oldValue) {
					this.removeCoupon();
					// remove previous search results
					this.items = null;
				}
				if (value !== '') {
					this.checkForCoupon(value);
					return;
				}
				this.hideSearch();
			},
			resetField() {
				this.searching = false;
				this.applied = false;
				this.coupon = null;
				this.message = null;
			},
			checkForCoupon(code) {
				this.resetField();
				this.searching = true;
				this.showSearch();
				this.useCoupon(code);
			},
			displaySearch(newHeight, completion) {
				const messageElement = this.$refs.couponContainer;
				Velocity(messageElement, {
					height: newHeight,
				},{
					duration: displayAnimation,
					ease: 'easeInOut',
					complete: () => {
						if (completion) {
							completion();
						}
					}
				});
			},
			showSearch() {
				let defaultHeight = (!this.el.is.expandedView) ? 9 : 14;
				let remString = `${defaultHeight}rem`;

				if (this.items) {
					const length = this.items.length;
					let height = (length * defaultHeight) + 4;
					height = Math.max(height, defaultHeight);
					remString = `${height}rem`;
				}
				this.displaySearch(remString);
			},
			hideSearch() {
				this.displaySearch('0rem', this.resetField);
			},
			displayMessage() {
				const message = this.message;
				if (message) {
					return message;
				}
			},
			updateCouponField() {
				const coupon = this.getCoupon;
				const input = this.$refs.couponInput;
				if (coupon) {
					this.coupon = coupon;
					input.value = coupon.codeName || '';
					this.checkForCoupon(input.value);
				}
			},
			updateCouponDisplay() {
				const self = this;
				setTimeout(() => {
					if (self.applied) {
						self.showSearch();
					}
				}, 0);
			},
			handleRemoveCoupon() {
				const input = this.$refs.couponInput;
				if (!input) { return; }
				input.value = '';
				this.removeCoupon();
				this.resetField();
				this.hideSearch();
				EventBus.$emit('recalculate-order');
			},
			updateOnItemRemoved(item) {
				const cartItems = this.getCartItems;
				const input = this.$refs.couponInput;
				const coupon = this.getCoupon;
				let itemWithCoupon = -1;
				if (coupon && coupon.target === 'order') {
					this.updateCouponField();
					return;
				}
				if (coupon && coupon.minItemCount > 1) {
					if (this.itemMatchesForCoupon(item, coupon)) {
						itemWithCoupon = findIndex(cartItems, (item) => {
							return item.hasCoupon();
						});
					}
				} else {
					itemWithCoupon = (item.hasCoupon()) ? 1 : -1;
				}
				if (itemWithCoupon !== -1) {
					const indexes = this.applyDiscountToCartItems(coupon);
					const applicableItems = this.getApplicableCartItems();
					const applicableCount = applicableItems.length;
					if (!indexes.includes(-1) && applicableCount === 1) {
						this.handleRemoveCoupon();
						return;
					}
					this.updateCouponField();
				}
			},
			updateAfterItemRemoved() {
				const coupon = this.getCoupon;
				const validateCoupon = this.validateCouponOrder(coupon);
				if (!validateCoupon) {
					this.handleRemoveCoupon();
					return;
				}
				this.handleNewCoupon(coupon);
			},
			applyCouponDiscount(discount) {
				const name = discount.name;
				this.message = CouponMessages.APPLIED(name);
				this.items = null;
				this.applied = true;
				discount.active = true;
				this.setCoupon(discount);
			},
			handleItemBasedCoupon(discount) {
				const indexes = this.applyDiscountToCartItems(discount);
				const couponIsApplied = find(indexes, (val) => val !== -1);
				
				// if we have value !== 1 in indexes, coupon was applied to some item in the list
				if (couponIsApplied !== -1 && couponIsApplied !== undefined) {
					// apply coupon
					this.applyCouponDiscount(discount);
					
					const isMultiRedemption = 1 < discount.maxRedemptions;
					
					// unlimited redemptions or redemptions still available should not hide list
					if (isMultiRedemption && !indexes.includes(-1) || !isMultiRedemption && discount.maxRedemptions !== 0 ) {
						return;
					}
				}
				// show item to add
				this.items = this.getItemsInCoupon(discount, true);

				if (this.items.length === 0) {
					this.message = CouponMessages.NO_ITEMS;
					return;
				} else {
					this.items = this.items.map((item) => {
						let restrictionTxt = this.getItemRestrictionTxt(item, discount);
						if (restrictionTxt) {
							restrictionTxt = `<br/><br/> <i>Coupon Restrictions</i>: ${restrictionTxt}`;
							let cartItemModel = new CartItemModel(item);
							cartItemModel.addDiscount(discount);
							cartItemModel.description = `${item.description} ${restrictionTxt}`;
							return cartItemModel;
						} else {
							return {...item}
						}
					})
				}
				const vue = this;
				if (discount) {
					setTimeout(() => {
						vue.showSearch();
					}, displayAnimation);
				}
			},
			handleOrderBasedCoupon(discount) {
				const hasDiscounts = this.hasDiscountedItems();
				if (hasDiscounts) {
					this.message = CouponMessages.ERROR_EXISTING_DISCOUNTS;
					return;
				}
				this.setCoupon(discount);
				this.applyCouponDiscount(discount);
			},
			handleMinSubtotalDiscount(coupon) {
				const minSubtotal = +(coupon.minSubtotal);
				if (!minSubtotal) {
					return true;
				}
				const cartTotal = this.cartPrice;
				if (minSubtotal <= cartTotal) {
					return true;
				}
				return false;
			},
			handleAfterSubtotalDiscount(coupon) {
				const cartTotal = this.cartPrice;
				const itemPrice = this.getHighestPriceInCoupon(coupon);
				const hasItem = this.getApplicableCartItems(coupon);
				const minSubtotal = +(coupon.minSubtotal);
				let useSubtotal = (hasItem.length > 0) ? minSubtotal + itemPrice : minSubtotal;
				if (useSubtotal <= cartTotal) {
					return true;
				}
				return false;
			},
			validateCouponOrder(coupon) {
				if (!coupon) {
					return true;
				}
				const afterSubtotal = coupon.minAfterSubtotal;
				if (afterSubtotal) {
					return this.handleAfterSubtotalDiscount(coupon);
				}
				return this.handleMinSubtotalDiscount(coupon);
			},
			deactivateCoupon(_coupon) {
				const coupon = this.getCoupon || _coupon;
				coupon.active = false;
				this.setCoupon(coupon);
			},
			handleNewCoupon(newCoupon) {
				if (newCoupon) {
					const target = newCoupon.target;
					const validCouponForOrder = this.validateCouponOrder(newCoupon);
					if (!validCouponForOrder) {
						const minSubtotal = +(newCoupon.minSubtotal);
						const formattedSubtotal = Formatter.centsToDollars(minSubtotal);
						if (newCoupon.minAfterSubtotal) {
							this.message = CouponMessages.ERROR_MINIMUM_AFTER_SUBTOTAL(formattedSubtotal);
							this.deactivateCoupon(newCoupon);
							return;
						}
						this.message = CouponMessages.ERROR_MINIMUM_SUBTOTAL(formattedSubtotal);
						this.deactivateCoupon(newCoupon);
						return;
					}
					// check order sub total reqs
					switch (target) {
						case 'item':
							this.handleItemBasedCoupon(newCoupon);
							break;
						case 'order':
							this.handleOrderBasedCoupon(newCoupon);
							break;
						default:
							break;
					}
				}
				EventBus.$emit('recalculate-order');
			}
		},
		watch: {
			coupon(newCoupon, oldValue) {
				if (newCoupon) {
					this.handleNewCoupon(newCoupon);
				} else {
					this.handleRemoveCoupon();
				}
				EventBus.$emit('recalculate-order');
			}
		},
		mounted() {
			EventBus.$on('item-added-coupon', this.updateCouponField);
			EventBus.$on('item-removed', this.updateOnItemRemoved);
			EventBus.$on('update-coupon-display', this.updateCouponDisplay);
			EventBus.$on('after-item-removed', this.updateAfterItemRemoved);
			EventBus.$on('store-changed', this.handleRemoveCoupon);
			if (this.coupon) {
				this.handleNewCoupon(this.coupon)
			}
			this.updateCouponField();
		},
		destroyed() {
			EventBus.$off('item-added-coupon');
			EventBus.$off('item-removed');
			EventBus.$off('update-coupon-display');
			EventBus.$off('after-item-removed');
		}
	}
</script>

<style lang="scss" scoped>
	@import 'globalStyles';
	.coupon-container {
		display: flex;
		flex-direction: column;

		width: 100%;
		max-width: 924px;

		a {
			font-weight: bold;
			color: $storeButtonColor;
		}
		.coupon-input {
			width: 100%;
		}
		.coupon-applied {
			display: flex;
			align-items: center;
			margin: 0.5rem;
			flex-wrap: wrap;
			color: green;
		}
		.coupon-icon-wrapper {
			margin-left: auto;
    		padding-right: 1rem;
		}
		.coupon-message-container {
			height: 0;
			max-height: 40rem;

			overflow: hidden;
			overflow-y: scroll;
			width: 100%;
			margin: auto;

			.coupon-message {
				margin: 1.5rem 0;
				max-width: $maxWidthContainerDesktop;
			}
		}
		&.expanded {
			justify-content: center;
			align-items: center;
			max-width: $maxWidthContainerDesktop;
   			margin: 0 auto;
			.coupon-input {
				width: $maxWidthContainerDesktop;
				margin: 0 auto;
			}
		}
		#redemptions-available {
			margin: 0.5rem 0rem;
		}
	}
</style>


// WEBPACK FOOTER //
// src/components/checkout/CouponField.vue